import { Injectable } from '@angular/core';
import { BehaviorSubject, map, Observable, tap } from 'rxjs';

import {
  ContentJsonType,
  ContentType,
  FindSeoByTargetCacheGQL,
  GetGeneralSeojsonGQL,
  GetMainCategorySeojsonGQL,
  GetMainPageSeojsonGQL,
  GetPagesSeojsonGQL,
  GetSimpleProductSeojsonGQL,
  GetSubCategorySeojsonGQL,
  GetBlogSeojsonGQL,
  SeoInput,
  SeoType,
} from '@sifca-monorepo/ecommerce-generator';
import { StorageHelper } from '@diktup/frontend/helpers';

@Injectable({
  providedIn: 'root',
})
export class SeoService {
  private seo: BehaviorSubject<SeoType> = new BehaviorSubject(null);
  private productSeo: BehaviorSubject<ContentJsonType> = new BehaviorSubject(null);

  constructor(
    private storageHelper: StorageHelper,
    private getPagesSeojsonGQL: GetPagesSeojsonGQL,
    private findSEOByTargetGQL: FindSeoByTargetCacheGQL,
    private getGeneralSeojsonGQL: GetGeneralSeojsonGQL,
    private getMainPageSEOjsonGQL: GetMainPageSeojsonGQL,
    private getBlogSeojsonGQL: GetBlogSeojsonGQL,
    private getSubCategorySeojsonGQL: GetSubCategorySeojsonGQL,
    private getMainCategorySeojsonGQL: GetMainCategorySeojsonGQL,
    private getSimpleProductSeojsonGQL: GetSimpleProductSeojsonGQL,
  ) {}

  get seo$(): Observable<SeoType> {
    return this.seo.asObservable();
  }

  get productSeo$(): Observable<ContentJsonType> {
    return this.productSeo.asObservable();
  }

  getSeo(pos: string): Observable<SeoType> {
    return this.findSEOByTargetGQL
      .fetch({
        target: { pos },
      })
      .pipe(
        tap((response: any) => {
          if (response.data) {
            this.seo.next(response.data.findSEOByTargetCache);
            return response.data.findSEOByTargetCache;
          }
        }),
      );
  }

  findSEOByTarget(pos: string): Observable<SeoInput> {
    return this.findSEOByTargetGQL.fetch({ target: { pos } }).pipe(
      map(({ data }: any) => {
        return data?.findSEOByTarget;
      }),
    );
  }

  getMainPageSEOJSON(pos: string): Observable<any> {
    return this.getMainPageSEOjsonGQL.fetch({ target: { pos } }).pipe(
      map(({ data }: any) => {
        return data.getMainPageSEOJSON;
      }),
    );
  }

  getBlogSeoJSON(blogId: string): Observable<any> {
    return this.getBlogSeojsonGQL.fetch({ blogId }).pipe(
      map(({ data }: any) => {
        return data.getBlogSEOJSON;
      }),
    );
  }

  getSimpleProductSEOJSON(pos: string, barcodeId: string): Observable<ContentJsonType> {
    return this.getSimpleProductSeojsonGQL.fetch({ barcodeId, target: { pos } }).pipe(
      map(({ data }: any) => {
        if (data) {
          this.productSeo.next(data.getSimpleProductSEOJSON);
          return data.getSimpleProductSEOJSON;
        }
      }),
    );
  }

  getPagesSEOJSON(pageId: string): Observable<ContentJsonType> {
    return this.getPagesSeojsonGQL.fetch({ pageId }).pipe(
      map(({ data }: any) => {
        return data.getPagesSEOJSON;
      }),
    );
  }

  getGeneralSEOJSON(): Observable<any> {
    return this.getGeneralSeojsonGQL.fetch({ target: { pos: this.storageHelper.getData('posId') } }).pipe(
      map(({ data }: any) => {
        return data.getGeneralSEOJSON;
      }),
    );
  }

  getMainCategorySEOJSON(categoryId: string): Observable<ContentType> {
    return this.getMainCategorySeojsonGQL.fetch({ categoryId }).pipe(
      map(({ data }: any) => {
        return data.getMainCategorySEOJSON;
      }),
    );
  }

  getSubCategorySEO(categoryId: string): Observable<ContentType> {
    return this.getSubCategorySeojsonGQL.fetch({ categoryId }).pipe(
      map(({ data }: any) => {
        return data.getSubCategorySeojsonGQL;
      }),
    );
  }
}
